"use client"

import { useState } from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { timeTheftSchema, TimeTheftFormValues } from "@/utils/helpers/validators/calculators/time-calculator.schema"
import { Metric, Bar, fmtCurrency } from "../SmallComponents"
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import Button from "@/components/ui/Button"
import { Routes } from "@/lib/routes"
import Link from "next/link"

type Result = {
   weeklyHours: number
   annualLossPerStore: number
   totalAnnualLoss: number
   annualSavings: number
} | null

export default function TimeTheftCalculator() {
   const [results, setResults] = useState<Result>(null)

   const form = useForm<TimeTheftFormValues>({
      resolver: zodResolver(timeTheftSchema),
      defaultValues: {
         wage: 16,
         minutes: 8,
         employees: 25,
         shifts: 7,
         locations: 1,
      },
   })

   const wage = form.watch("wage")
   const employees = form.watch("employees")
   const locations = form.watch("locations")

   function onSubmit(data: TimeTheftFormValues) {
      const hoursPerEmpWeek = (data.minutes / 60) * data.shifts
      const hoursStoreWeek = hoursPerEmpWeek * data.employees

      const costWeek = hoursStoreWeek * data.wage

      const annualLossPerStore = costWeek * 52
      const totalAnnualLoss = annualLossPerStore * data.locations

      const annualSavings = totalAnnualLoss * 0.85

      setResults({
         weeklyHours: hoursStoreWeek,
         annualLossPerStore,
         totalAnnualLoss,
         annualSavings,
      })
   }

   function reset() {
      form.reset()
      setResults(null)
   }

   const currentCost = results?.totalAnnualLoss ?? 0
   const remainingCost = results ? results.totalAnnualLoss - results.annualSavings : 0

   const beforeWidth = 100

   const afterWidth = currentCost > 0 ? (remainingCost / currentCost) * 100 : 0

   return (
      <div className="mx-auto grid max-w-6xl gap-6 px-3 py-12 md:grid-cols-[60%_40%]" id="time-theft-calculator">
         {/* INPUT CARD */}

         <Card className="rounded-2xl shadow-lg">
            <CardHeader>
               <CardTitle className="text-center text-lg font-semibold">Input your operation</CardTitle>

               <CardDescription>Estimate how much time theft is costing your business.</CardDescription>

               <div className="h-[2px] w-full rounded-full bg-muted" />
            </CardHeader>

            <CardContent className="space-y-4">
               <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                  <div>
                     <label className="text-sm text-muted-foreground">Average Hourly Wage ($)</label>
                     <Input type="number" {...form.register("wage", { valueAsNumber: true })} />
                     <p className="text-xs text-red-500">{form.formState.errors.wage?.message}</p>
                  </div>
                  <div>
                     <label className="text-sm text-muted-foreground">Minutes Stolen Per Employee Per Shift</label>
                     <Input type="number" {...form.register("minutes", { valueAsNumber: true })} />
                     <p className="text-xs text-red-500">{form.formState.errors.minutes?.message}</p>
                  </div>
                  <div>
                     <label className="text-sm text-muted-foreground">Employees Per Location</label>
                     <Input type="number" {...form.register("employees", { valueAsNumber: true })} />
                     <p className="text-xs text-red-500">{form.formState.errors.employees?.message}</p>
                  </div>
                  <div>
                     <label className="text-sm text-muted-foreground">Shifts Per Week</label>
                     <Input type="number" {...form.register("shifts", { valueAsNumber: true })} />
                     <p className="text-xs text-red-500">{form.formState.errors.shifts?.message}</p>
                  </div>
                  <div>
                     <label className="text-sm text-muted-foreground">Locations</label>
                     <Input type="number" {...form.register("locations", { valueAsNumber: true })} />
                     <p className="text-xs text-red-500">{form.formState.errors.locations?.message}</p>
                  </div>

                  <div className="flex gap-3">
                     <Button type="submit" className="flex-1 bg-[#196EB9] text-white hover:bg-[#155a92]">
                        Calculate
                     </Button>

                     <Button
                        type="button"
                        onClick={reset}
                        className="flex-1 bg-[#EB5A23] text-white hover:bg-[#c1481b]"
                     >
                        Reset
                     </Button>
                  </div>
               </form>
            </CardContent>
         </Card>

         {/* RESULTS CARD */}

         <Card className="rounded-2xl shadow-lg">
            <CardHeader>
               <CardTitle className="text-center text-lg font-semibold">Time Theft Impact</CardTitle>

               <CardDescription>Estimated labor lost due to time theft.</CardDescription>

               <div className="h-[2px] w-full rounded-full bg-muted" />
            </CardHeader>

            <CardContent className="space-y-4">
               {!results && <p className="text-sm text-muted-foreground">Enter your numbers and click calculate.</p>}

               {results && (
                  <>
                     <div className="grid gap-4 lg:grid-cols-2">
                        <Metric label="Weekly Hours Lost" value={`${results.weeklyHours.toFixed(1)} hrs`} />
                        <Metric label="Annual Loss / Store" value={results.annualLossPerStore} type="currency" />
                        <Metric
                           label={"Total Annual Loss"}
                           subtitle={`(${locations} ${locations === 1 ? "store" : "stores"})`}
                           value={results.totalAnnualLoss}
                           type="currency"
                        />
                        <Metric label="Savings With AI Clock" value={results.annualSavings} type="currency" />
                     </div>

                     {/* BARS */}

                     <div className="space-y-3">
                        <Bar
                           label="Current Time Theft Cost"
                           value={currentCost}
                           type="currency"
                           width={beforeWidth}
                           color="bg-red-500"
                        />
                        <Bar
                           label="Remaining Loss After AI Clock"
                           value={remainingCost}
                           type="currency"
                           width={afterWidth}
                           color="bg-green-500"
                        />
                     </div>

                     {/* SUMMARY */}

                     <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
                        With an average wage of <strong>${wage}</strong> and <strong>{employees}</strong> employees per
                        store, time theft could cost roughly <strong>{fmtCurrency(results.totalAnnualLoss)}</strong> per
                        year across <strong>{locations}</strong> locations. Implementing an AI Clock could recover
                        around <strong>{fmtCurrency(results.annualSavings)}</strong> annually.
                        <Link href={Routes.client.contact.getPath()}>
                           <Button className="mt-2 w-full rounded-xl bg-[#196EB9] text-white hover:bg-[#155a92]">
                              Claim Free AI Clock for 60 Days
                           </Button>
                        </Link>
                     </div>
                  </>
               )}
            </CardContent>
         </Card>
      </div>
   )
}
